// auth.ts

export interface User {
  username: string;
  role: string;
  displayName: string;
  /** Alias for displayName — kept for backward compat */
  viewer: string;
  brand: string;
  brands: string[];
  categories?: string[];
  viewMode?: string;
}

let currentUser: User | null = null;

const AUTH_VERSION = "3";

const storedVersion = localStorage.getItem("auth_version");
if (storedVersion !== AUTH_VERSION) {
  localStorage.removeItem("currentUser");
  localStorage.removeItem("auth_token");
  localStorage.removeItem("refresh_token");
  localStorage.setItem("auth_version", AUTH_VERSION);
}

export async function login(username: string, password: string): Promise<User> {
  const res = await fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || "Invalid credentials");
  }

  const { token, refreshToken, user } = await res.json();

  const normalized: User = { ...user, viewer: user.displayName };

  currentUser = normalized;
  localStorage.setItem("auth_token",    token);
  localStorage.setItem("refresh_token", refreshToken);
  localStorage.setItem("currentUser",   JSON.stringify(normalized));
  localStorage.setItem("auth_version",  AUTH_VERSION);

  return normalized;
}

export async function logout(): Promise<void> {
  const refreshToken = localStorage.getItem("refresh_token");

  // Best-effort: tell the backend to invalidate the refresh token
  if (refreshToken) {
    fetch("/api/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    }).catch(() => {/* ignore */});
  }

  currentUser = null;
  localStorage.removeItem("currentUser");
  localStorage.removeItem("auth_token");
  localStorage.removeItem("refresh_token");
}

export function getCurrentUser(): User | null {
  if (!currentUser) {
    const storedUser    = localStorage.getItem("currentUser");
    const storedVersion = localStorage.getItem("auth_version");
    if (storedUser && storedVersion === AUTH_VERSION) {
      currentUser = JSON.parse(storedUser);
    } else {
      localStorage.removeItem("currentUser");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      localStorage.setItem("auth_version", AUTH_VERSION);
    }
  }
  return currentUser;
}

export function getToken(): string | null {
  return localStorage.getItem("auth_token");
}

export function getRefreshToken(): string | null {
  return localStorage.getItem("refresh_token");
}

export function setTokens(token: string, refreshToken: string): void {
  localStorage.setItem("auth_token",    token);
  localStorage.setItem("refresh_token", refreshToken);
}
